import { Link } from "react-router-dom";
import { ArrowRight, Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { useCart, fmtPrice } from "@/lib/cart";
import { imgSrc } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

export default function CartPage() {
  const { items, setQty, remove, subtotal } = useCart();

  return (
    <div className="bg-cream min-h-[60vh]" data-testid="cart-page">
      <section className="container-site py-14 sm:py-20 max-w-3xl">
        <Reveal>
          <p className="label-eyebrow mb-4">Your Bag</p>
          <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl text-ink" data-testid="cart-headline">
            Shopping <span className="text-pool">Bag.</span>
          </h1>
        </Reveal>

        {items.length === 0 ? (
          <Reveal delay={0.1}>
            <div className="mt-10 bg-white rounded-2xl border border-slate-100 p-12 text-center" data-testid="cart-empty">
              <ShoppingBag className="h-10 w-10 text-pool/40 mx-auto" />
              <p className="mt-4 text-slate-500 text-sm">Your bag is empty.</p>
              <Link to="/shop" className="mt-6 inline-flex items-center gap-2.5 bg-pool hover:bg-pool-dark text-white font-bold text-xs tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-colors" data-testid="cart-shop-link">
                Shop Poolside <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        ) : (
          <>
            <div className="mt-10 bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100" data-testid="cart-items">
              {items.map((i) => (
                <div key={i.key} className="p-5 flex items-center gap-4" data-testid={`cart-item-${i.product_id}`}>
                  <div className="h-20 w-20 shrink-0 rounded-xl overflow-hidden bg-pool-light/30 flex items-center justify-center">
                    {i.image ? (
                      <img src={imgSrc(i.image)} alt={i.name} className="h-full w-full object-cover" />
                    ) : (
                      <img src="/logo.png" alt="" aria-hidden="true" className="h-8 w-auto opacity-60" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-black text-ink text-sm leading-tight">{i.name}</p>
                    {i.variant && <p className="text-xs text-slate-500 mt-0.5">{i.variant}</p>}
                    <p className="text-xs text-pool-deep font-bold mt-1">{fmtPrice(i.unit_price_cents)}</p>
                  </div>
                  <div className="inline-flex items-center border border-slate-200 rounded-full shrink-0">
                    <button onClick={() => setQty(i.key, i.quantity - 1)} className="p-2.5 text-slate-500 hover:text-pool" aria-label="Decrease" data-testid={`cart-minus-${i.product_id}`}><Minus className="h-3.5 w-3.5" /></button>
                    <span className="w-7 text-center text-xs font-bold" data-testid={`cart-qty-${i.product_id}`}>{i.quantity}</span>
                    <button onClick={() => setQty(i.key, i.quantity + 1)} className="p-2.5 text-slate-500 hover:text-pool" aria-label="Increase" data-testid={`cart-plus-${i.product_id}`}><Plus className="h-3.5 w-3.5" /></button>
                  </div>
                  <button onClick={() => remove(i.key)} className="p-2 text-slate-300 hover:text-red-500 transition-colors shrink-0" aria-label="Remove" data-testid={`cart-remove-${i.product_id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-white rounded-2xl border border-slate-100 p-6" data-testid="cart-summary">
              <div className="flex items-center justify-between">
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Subtotal</p>
                <p className="font-display font-black text-2xl text-ink" data-testid="cart-subtotal">{fmtPrice(subtotal)}</p>
              </div>
              <p className="mt-2 text-xs text-slate-400">Taxes and shipping calculated at checkout. Free pickup at the Poolside Cooking Café.</p>
              <Link
                to="/checkout"
                className="mt-6 w-full inline-flex items-center justify-center gap-2.5 bg-pool hover:bg-pool-dark text-white font-bold text-xs tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-colors"
                data-testid="cart-checkout-cta"
              >
                Proceed to Checkout <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
